// ── Local player identity used for the realtime session. ──
// In demo mode we generate a stable random id/username and persist it so reconnects
// keep the same handle. When a wallet is connected, the wallet drives identity.

import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';

const LS_KEY = 'mn.identity';

export interface Identity {
  playerId: string;
  username: string;
  avatar: string;
  wallet: string | null;
}

const ADJECTIVES = ['Wild', 'Iron', 'Ember', 'Frost', 'Storm', 'Dusk', 'Verdant', 'Stone'];
const NOUNS = ['Hunter', 'Tamer', 'Ranger', 'Tracker', 'Warden', 'Nomad', 'Seeker', 'Drifter'];

function randomId(): string {
  return (
    'plr_' +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-4)
  );
}

function randomUsername(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const n = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${a}${n}${Math.floor(Math.random() * 1000)}`;
}

function loadStored(): Partial<Identity> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Partial<Identity>) : {};
  } catch {
    return {};
  }
}

function persist(identity: Identity): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(identity));
  } catch {
    /* ignore (private mode etc.) */
  }
}

/**
 * Resolve a stable identity for this browser. If a wallet is provided it becomes the
 * canonical playerId (truncated for display); otherwise a persisted demo identity is used.
 */
export function resolveIdentity(wallet: string | null): Identity {
  const stored = loadStored();

  const playerId = wallet ?? stored.playerId ?? randomId();
  // Prefer the REAL account display name (set on login) so other players see a username over your head
  // — not a truncated wallet. Falls back to a stored handle, then a short wallet, then a random guest.
  const realName = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME);
  const username =
    (typeof realName === 'string' && realName.trim()) ||
    stored.username ||
    (wallet ? `${wallet.slice(0, 4)}…${wallet.slice(-4)}` : randomUsername());
  const avatar = stored.avatar ?? 'default';

  const identity: Identity = { playerId, username, avatar, wallet: wallet ?? null };
  persist(identity);
  return identity;
}

/**
 * The display name to send on a realtime join: the LIVE account name if it's set now, otherwise the
 * identity's stored handle. resolveIdentity() runs once (often before login / during guest entry), so
 * the baked name can be a stale 'Spectator' / wallet; re-reading PLAYER_NAME at join time means other
 * players always see the current name. Keeps the cached + persisted identity in sync as a side effect.
 */
export function liveUsername(identity: Identity): string {
  const realName = dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME);
  const name = (typeof realName === 'string' && realName.trim()) || identity.username;
  if (name && name !== identity.username) {
    identity.username = name;
    persist(identity);
  }
  return name;
}
